import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, copyOrWarn } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Qr = ({ url, src }) => (
  <img
    data-testid="twofa-qr"
    alt="QR code"
    className="h-48 w-48 rounded-lg border border-slate-200 bg-white p-2"
    src={src || `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`}
  />
);

export default function Security() {
  const { user, refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setEnabled(!!user?.twofa_enabled); }, [user]);

  const start = async () => {
    setBusy(true);
    try { setSetup((await api.post("/auth/2fa/setup")).data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setRecovery(data.recovery_codes);
      setSetup(null);
      setCode("");
      setEnabled(true);
      await refreshUser();
      toast.success("Double authentification activée");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    const c = window.prompt("Entrez un code de votre application d'authentification pour désactiver la 2FA :");
    if (!c) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: c });
      setEnabled(false);
      await refreshUser();
      toast.success("Double authentification désactivée");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto px-6 lg:px-10 py-8 max-w-2xl" data-testid="security-page">
      <p className="text-xs uppercase tracking-wider text-slate-500">SÉCURITÉ</p>
      <h1 className="text-2xl font-semibold mb-6">Double authentification</h1>

      {recovery ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-6" data-testid="recovery-codes">
          <p className="font-semibold text-green-800 flex items-center gap-2"><Check className="h-5 w-5" /> 2FA activée</p>
          <p className="text-sm text-slate-600 mt-2 mb-4">Conservez ces codes de secours en lieu sûr : ils permettent de vous connecter si vous perdez votre téléphone. Chacun n'est utilisable qu'une fois.</p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {recovery.map((c) => <div key={c} className="bg-white border border-slate-200 rounded px-3 py-2 text-center">{c}</div>)}
          </div>
          <Button variant="secondary" className="mt-4" onClick={() => copyOrWarn(recovery.join("\n"))} data-testid="copy-recovery-btn"><Copy className="h-4 w-4 mr-2" /> Copier les codes</Button>
          <Button className="mt-4 ml-2" onClick={() => setRecovery(null)} data-testid="recovery-done-btn">J'ai enregistré mes codes</Button>
        </div>
      ) : enabled ? (
        <div className="rounded-lg border border-slate-200 p-6" data-testid="twofa-active">
          <p className="flex items-center gap-2 text-green-700 font-medium"><ShieldCheck className="h-5 w-5" /> La double authentification est activée sur votre compte.</p>
          <p className="text-sm text-slate-500 mt-2 mb-4">À chaque connexion, un code à 6 chiffres de votre application vous sera demandé.</p>
          <Button variant="destructive" onClick={disable} disabled={busy} data-testid="disable-2fa-btn"><ShieldOff className="h-4 w-4 mr-2" /> Désactiver</Button>
        </div>
      ) : setup ? (
        <div className="rounded-lg border border-slate-200 p-6 space-y-4" data-testid="twofa-setup">
          <p className="text-sm text-slate-600">1. Scannez ce QR code avec Google Authenticator, Microsoft Authenticator ou Authy.</p>
          <Qr url={setup.otpauth_url} src={setup.qr} />
          <p className="text-sm text-slate-600">Ou saisissez cette clé manuellement : <code className="font-mono bg-slate-100 px-2 py-0.5 rounded text-xs" data-testid="twofa-secret">{setup.secret}</code></p>
          <p className="text-sm text-slate-600">2. Entrez le code à 6 chiffres affiché :</p>
          <div className="flex gap-2 max-w-xs">
            <Input data-testid="enable-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} className="text-center text-xl tracking-[0.3em] font-mono" />
            <Button onClick={enable} disabled={busy || code.length < 6} data-testid="confirm-2fa-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activer"}</Button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 p-6" data-testid="twofa-inactive">
          <p className="text-slate-600 mb-4">Ajoutez une étape de sécurité : en plus de votre mot de passe, un code temporaire généré par votre téléphone sera demandé à la connexion.</p>
          <Button onClick={start} disabled={busy} data-testid="enable-2fa-btn"><ShieldCheck className="h-4 w-4 mr-2" /> Activer la double authentification</Button>
        </div>
      )}
    </div>
  );
}
